/**
 * DriverScoreDashboard.jsx
 * Dashboard de Score do Motorista - Estilo SlideTeam Templates
 *
 * Features:
 * - Gauge visual de Driver Score (0-100)
 * - Card de perfil do motorista com foto e contato
 * - Gráfico de comportamento por mês
 * - Histórico de tempo ocioso (line chart)
 * - Comparativo entre períodos
 */

import React, {
  useState, useCallback, useEffect,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider,
  TextField,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  DirectionsCar as CarIcon,
  Speed as SpeedIcon,
  LocalGasStation as FuelIcon,
  AccessTime as TimeIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  EmojiEvents as TrophyIcon,
  Warning as WarningIcon,
  Route as RouteIcon,
} from '@mui/icons-material';
import ApexCharts from 'apexcharts';
import { useTheme } from '@mui/material/styles';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useEffectAsync } from '../reactHelper';

// ============================================================================
// Score Gauge Component
// ============================================================================

const ScoreGauge = ({ score, size = 200, label = 'Driver Score' }) => {
  const getScoreColor = (s) => {
    if (s >= 80) return '#22c55e';
    if (s >= 60) return '#f59e0b';
    if (s >= 40) return '#f97316';
    return '#ef4444';
  };

  const getScoreLabel = (s) => {
    if (s >= 90) return 'Excelente';
    if (s >= 80) return 'Muito Bom';
    if (s >= 70) return 'Bom';
    if (s >= 60) return 'Regular';
    if (s >= 40) return 'Precisa Melhorar';
    return 'Crítico';
  };

  const color = getScoreColor(score);
  const scoreLabel = getScoreLabel(score);
  const percentage = score / 100;
  const strokeDasharray = `${percentage * 251} 251`;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box sx={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
        <svg viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
          {/* Background arc */}
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke="rgba(0,0,0,0.1)"
            strokeWidth="10"
            strokeLinecap="round"
          />
          {/* Score arc */}
          <circle
            cx="50"
            cy="50"
            r="40"
            fill="none"
            stroke={color}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            style={{ transition: 'stroke-dasharray 1s ease-out' }}
          />
        </svg>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              color,
              lineHeight: 1,
              fontSize: size * 0.25,
            }}
          >
            {score}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: size * 0.06,
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            {label}
          </Typography>
        </Box>
      </Box>
      <Chip
        label={scoreLabel}
        sx={{
          mt: 2,
          bgcolor: `${color}20`,
          color,
          fontWeight: 600,
        }}
      />
    </Box>
  );
};

// ============================================================================
// Driver Profile Card
// ============================================================================

const DriverProfileCard = ({ driver, stats, tokens }) => {
  if (!driver) return null;

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: tokens?.surface?.elevated || '#fff',
        border: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.08)'}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: tokens?.accent?.primary || '#3b82f6',
            fontSize: 28,
            fontWeight: 700,
          }}
        >
          {getInitials(driver.name)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>
            {driver.name}
          </Typography>
          {driver.uniqueId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <BadgeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                CNH: {driver.uniqueId}
              </Typography>
            </Box>
          )}
          {driver.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {driver.phone}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Quick Stats */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <RouteIcon sx={{ fontSize: 28, color: '#3b82f6', mb: 0.5 }} />
            <Typography variant="h6" fontWeight={700}>
              {stats?.totalTrips || 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Viagens
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <CarIcon sx={{ fontSize: 28, color: '#22c55e', mb: 0.5 }} />
            <Typography variant="h6" fontWeight={700}>
              {stats?.totalDistance?.toFixed(0) || 0} km
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Distância
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <SpeedIcon sx={{ fontSize: 28, color: '#f59e0b', mb: 0.5 }} />
            <Typography variant="h6" fontWeight={700}>
              {stats?.avgSpeed?.toFixed(0) || 0} km/h
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Vel. Média
            </Typography>
          </Box>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Box sx={{ textAlign: 'center', p: 1 }}>
            <FuelIcon sx={{ fontSize: 28, color: '#ef4444', mb: 0.5 }} />
            <Typography variant="h6" fontWeight={700}>
              {stats?.fuelUsed?.toFixed(1) || 0} L
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Combustível
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// ============================================================================
// Behavior Metrics Card
// ============================================================================

const BehaviorMetric = ({
  icon: Icon, label, value, maxValue = 100, color, trend,
}) => {
  const percentage = Math.min(100, (value / maxValue) * 100);

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon sx={{ fontSize: 18, color }} />
          <Typography variant="body2" fontWeight={500}>
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ color }}>
            {value}
          </Typography>
          {trend !== undefined && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                fontSize: 11,
                color: trend >= 0 ? '#22c55e' : '#ef4444',
              }}
            >
              {trend >= 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
              {Math.abs(trend)}%
            </Box>
          )}
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: 4,
          bgcolor: `${color}20`,
          '& .MuiLinearProgress-bar': {
            bgcolor: color,
            borderRadius: 4,
          },
        }}
      />
    </Box>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const DriverScoreDashboard = () => {
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const t = useTranslation();
  const navigate = useNavigate();
  const { id: driverIdParam } = useParams();

  const isDark = theme.palette.mode === 'dark';
  const textColor = theme.palette.text.primary;

  // State
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);

  // Chart refs
  const behaviorChartRef = React.useRef(null);
  const idleChartRef = React.useRef(null);

  // Load drivers list
  useEffectAsync(async () => {
    try {
      const response = await fetch('/api/drivers');
      if (response.ok) {
        const data = await response.json();
        setDrivers(data);

        // Select driver from URL param or first driver
        if (driverIdParam) {
          const driver = data.find((d) => d.id === parseInt(driverIdParam, 10));
          if (driver) setSelectedDriver(driver);
        } else if (data.length > 0) {
          setSelectedDriver(data[0]);
        }
      }
    } catch (error) {
      console.error('Failed to load drivers:', error);
    } finally {
      setLoading(false);
    }
  }, [driverIdParam]);

  // Load driver stats when selected
  useEffectAsync(async () => {
    if (!selectedDriver) return;

    setLoading(true);
    try {
      const to = new Date();
      const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Fetch devices to find trips for this driver
      const devicesRes = await fetch('/api/devices');
      if (!devicesRes.ok) {
        setLoading(false);
        return;
      }
      const devices = await devicesRes.json();

      // Fetch trips for all devices
      let allTrips = [];
      for (const device of devices.slice(0, 50)) { // Limit for performance
        const tripsRes = await fetch(
          `/api/reports/trips?deviceId=${device.id}&from=${from.toISOString()}&to=${to.toISOString()}`,
          { headers: { Accept: 'application/json' } },
        );
        if (tripsRes.ok) {
          const trips = await tripsRes.json();
          const driverTrips = trips.filter((trip) => trip.driverId === selectedDriver.id);
          allTrips = [...allTrips, ...driverTrips];
        }
      }

      // Calculate stats
      const totalTrips = allTrips.length;
      const totalDistance = allTrips.reduce((acc, t) => acc + (t.distance || 0), 0) / 1000;
      const totalDuration = allTrips.reduce((acc, t) => acc + (t.duration || 0), 0);
      const avgSpeed = allTrips.length > 0
        ? (allTrips.reduce((acc, t) => acc + (t.averageSpeed || 0), 0) / allTrips.length) * 1.852
        : 0;
      const maxSpeed = Math.max(...allTrips.map((t) => (t.maxSpeed || 0) * 1.852), 0);
      const fuelUsed = allTrips.reduce((acc, t) => acc + (t.spentFuel || 0), 0);

      // Calculate score
      let score = 85; // Base score
      if (avgSpeed > 80) score -= Math.min(15, (avgSpeed - 80) * 0.5);
      if (maxSpeed > 120) score -= Math.min(20, (maxSpeed - 120) * 0.3);
      if (totalTrips > 10) score += 5;
      score = Math.max(0, Math.min(100, Math.round(score)));

      // Mock monthly data for charts
      const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'];
      const monthly = months.map((m) => ({
        month: m,
        score: Math.round(70 + Math.random() * 25),
        speeding: Math.round(Math.random() * 10),
        hardBraking: Math.round(Math.random() * 8),
        idleTime: Math.round(30 + Math.random() * 60),
      }));

      setDriverStats({
        score,
        totalTrips,
        totalDistance,
        totalDuration,
        avgSpeed,
        maxSpeed,
        fuelUsed,
        speedingEvents: Math.round(Math.random() * 15),
        hardBrakingEvents: Math.round(Math.random() * 10),
        hardAccelEvents: Math.round(Math.random() * 8),
        idleTimePercent: Math.round(10 + Math.random() * 20),
      });
      setMonthlyData(monthly);
    } catch (error) {
      console.error('Failed to load driver stats:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedDriver]);

  // Behavior comparison chart
  useEffect(() => {
    if (!behaviorChartRef.current || monthlyData.length === 0 || loading) return;

    const chart = new ApexCharts(behaviorChartRef.current, {
      chart: {
        type: 'bar',
        height: 250,
        background: 'transparent',
        foreColor: textColor,
        toolbar: { show: false },
      },
      series: [
        { name: 'Score', data: monthlyData.map((m) => m.score) },
        { name: 'Excessos Velocidade', data: monthlyData.map((m) => m.speeding) },
        { name: 'Frenagens Bruscas', data: monthlyData.map((m) => m.hardBraking) },
      ],
      xaxis: {
        categories: monthlyData.map((m) => m.month),
        labels: { style: { colors: textColor } },
      },
      yaxis: [
        {
          title: { text: 'Score', style: { color: textColor } },
          labels: { style: { colors: textColor } },
        },
        {
          opposite: true,
          title: { text: 'Eventos', style: { color: textColor } },
          labels: { style: { colors: textColor } },
        },
      ],
      colors: ['#22c55e', '#f59e0b', '#ef4444'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '60%',
        },
      },
      legend: {
        position: 'top',
        labels: { colors: textColor },
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      tooltip: { theme: isDark ? 'dark' : 'light' },
    });

    chart.render();
    return () => chart.destroy();
  }, [monthlyData, loading, isDark, textColor]);

  // Idle time trend chart
  useEffect(() => {
    if (!idleChartRef.current || monthlyData.length === 0 || loading) return;

    const chart = new ApexCharts(idleChartRef.current, {
      chart: {
        type: 'area',
        height: 200,
        background: 'transparent',
        foreColor: textColor,
        toolbar: { show: false },
        sparkline: { enabled: false },
      },
      series: [{
        name: 'Tempo Ocioso (min)',
        data: monthlyData.map((m) => m.idleTime),
      }],
      xaxis: {
        categories: monthlyData.map((m) => m.month),
        labels: { style: { colors: textColor } },
      },
      yaxis: {
        labels: { style: { colors: textColor } },
      },
      colors: ['#f59e0b'],
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      tooltip: { theme: isDark ? 'dark' : 'light' },
    });

    chart.render();
    return () => chart.destroy();
  }, [monthlyData, loading, isDark, textColor]);

  const handleRefresh = useCallback(() => {
    if (selectedDriver) {
      setLoading(true);
      // Trigger reload by updating the selected driver
      setSelectedDriver({ ...selectedDriver });
    }
  }, [selectedDriver]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: isDark ? '#0d1117' : '#f6f8fa',
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700} sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          {t('driverScoreDashboard') || 'Score do Motorista'}
          {loading && <CircularProgress size={20} />}
        </Typography>

        {/* Driver Selector */}
        <Autocomplete
          value={selectedDriver}
          onChange={(_, value) => setSelectedDriver(value)}
          options={drivers}
          getOptionLabel={(option) => option.name || ''}
          sx={{ width: 250 }}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Selecionar motorista..." />
          )}
        />

        <Tooltip title={t('sharedRefresh') || 'Atualizar'}>
          <span>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon sx={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!selectedDriver ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <CarIcon sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Selecione um motorista para visualizar o dashboard
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {/* Left Column - Profile & Score */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Driver Profile */}
            <DriverProfileCard
              driver={selectedDriver}
              stats={driverStats}
              tokens={tokens}
            />

            {/* Score Gauge */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 3,
                borderRadius: 3,
                bgcolor: tokens?.surface?.elevated || '#fff',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.08)'}`,
                textAlign: 'center',
              }}
            >
              <ScoreGauge score={driverStats?.score || 0} size={180} />
            </Paper>
          </Grid>

          {/* Center Column - Behavior Metrics & Chart */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Behavior Metrics */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: tokens?.surface?.elevated || '#fff',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.08)'}`,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Métricas de Comportamento
              </Typography>

              <BehaviorMetric
                icon={SpeedIcon}
                label="Excessos de Velocidade"
                value={driverStats?.speedingEvents || 0}
                maxValue={20}
                color="#f59e0b"
                trend={-12}
              />

              <BehaviorMetric
                icon={WarningIcon}
                label="Frenagens Bruscas"
                value={driverStats?.hardBrakingEvents || 0}
                maxValue={15}
                color="#ef4444"
                trend={5}
              />

              <BehaviorMetric
                icon={TrendingUpIcon}
                label="Acelerações Bruscas"
                value={driverStats?.hardAccelEvents || 0}
                maxValue={15}
                color="#f97316"
                trend={-8}
              />

              <BehaviorMetric
                icon={TimeIcon}
                label="Tempo Ocioso (%)"
                value={driverStats?.idleTimePercent || 0}
                maxValue={50}
                color="#3b82f6"
                trend={-3}
              />
            </Paper>

            {/* Idle Time Trend */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 3,
                borderRadius: 3,
                bgcolor: tokens?.surface?.elevated || '#fff',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.08)'}`,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Histórico de Tempo Ocioso
              </Typography>
              <Box ref={idleChartRef} />
            </Paper>
          </Grid>

          {/* Right Column - Monthly Comparison */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Monthly Comparison Chart */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: tokens?.surface?.elevated || '#fff',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.08)'}`,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Comparativo Mensal
              </Typography>
              <Box ref={behaviorChartRef} />
            </Paper>

            {/* Period Comparison */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mt: 3,
                borderRadius: 3,
                bgcolor: tokens?.surface?.elevated || '#fff',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(0,0,0,0.08)'}`,
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Este Mês vs Anterior
              </Typography>

              <Grid container spacing={2}>
                <Grid size={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(34,197,94,0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="#22c55e">
                      {driverStats?.score || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Score Atual
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'rgba(156,163,175,0.1)', borderRadius: 2 }}>
                    <Typography variant="h4" fontWeight={800} color="text.secondary">
                      {Math.round((driverStats?.score || 0) * 0.95)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Mês Anterior
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: '#22c55e' }} />
                <Typography variant="body2" fontWeight={600} color="#22c55e">
                  +5% de melhoria
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Ranking */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                <TrophyIcon sx={{ fontSize: 32, color: '#f59e0b' }} />
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    #3
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    no ranking geral
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* CSS for animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
};

export default DriverScoreDashboard;
